import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot, type DocumentData } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { useDashboardAggregates } from '@/lib/useDashboardAggregates';
import type { DashboardInsightsDoc } from '@/lib/types';
import {
  computeHealth,
  fmtCurrency,
  fmtRelative,
  healthBand,
  stageMeta,
  type MissingField,
  type PipelineAccount,
  type StageKey,
} from '@/lib/pipelineAdapter';
import type {
  ActivityBucket,
  HealthBucketKey,
} from '@/lib/dashboardAggregates';
import {
  Avatar,
  Logo,
  StageChip,
} from '@/components/smartTable/atoms';

type DrilldownSelection =
  | { kind: 'kpi'; key: 'all' | 'at-risk' | 'stalled'; title: string }
  | { kind: 'funnel'; key: StageKey; title: string }
  | { kind: 'health'; key: HealthBucketKey; title: string }
  | { kind: 'blocker'; key: string; title: string }
  | { kind: 'missing'; key: MissingField; title: string }
  | { kind: 'activity'; key: ActivityBucket; title: string }
  | { kind: 'owner'; key: string; title: string };

const FONT =
  '"Inter", -apple-system, BlinkMacSystemFont, system-ui, sans-serif';

export function DashboardPage() {
  const { aggregates, accounts, loading } = useDashboardAggregates();
  const [drilldown, setDrilldown] = useState<DrilldownSelection | null>(null);
  const [insights, setInsights] = useState<DashboardInsightsDoc | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [refreshingInsights, setRefreshingInsights] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'meta', 'dashboardInsights'),
      (snap) => {
        setInsights(
          snap.exists()
            ? ((snap.data() as DocumentData) as DashboardInsightsDoc)
            : null,
        );
        setInsightsLoading(false);
      },
      (err) => {
        setInsightsError(err.message);
        setInsightsLoading(false);
      },
    );
    return unsub;
  }, []);

  const accountById = useMemo(() => {
    const map = new Map<string, PipelineAccount>();
    for (const a of accounts) map.set(a.id, a);
    return map;
  }, [accounts]);

  const drilldownAccounts = useMemo(() => {
    if (!drilldown) return [];
    let ids: string[] = [];
    switch (drilldown.kind) {
      case 'kpi':
        if (drilldown.key === 'all') ids = aggregates.kpis.allAccountIds;
        else if (drilldown.key === 'at-risk') ids = aggregates.kpis.atRiskAccountIds;
        else ids = aggregates.kpis.stalledAccountIds;
        break;
      case 'funnel':
        ids =
          aggregates.funnel.find((f) => f.stage === drilldown.key)?.accountIds ??
          [];
        break;
      case 'health':
        ids =
          aggregates.healthBuckets.find((h) => h.band === drilldown.key)
            ?.accountIds ?? [];
        break;
      case 'blocker':
        ids =
          aggregates.topBlockers.find((b) => b.token === drilldown.key)
            ?.accountIds ?? [];
        break;
      case 'missing':
        ids =
          aggregates.missingMatrix.find((m) => m.field === drilldown.key)
            ?.accountIds ?? [];
        break;
      case 'activity':
        ids =
          aggregates.activity.find((a) => a.bucket === drilldown.key)
            ?.accountIds ?? [];
        break;
      case 'owner':
        ids =
          aggregates.leaderboard.find((o) => o.ownerKey === drilldown.key)
            ?.accountIds ?? [];
        break;
    }
    return ids
      .map((id) => accountById.get(id))
      .filter((a): a is PipelineAccount => !!a);
  }, [drilldown, aggregates, accountById]);

  async function refreshInsights() {
    setRefreshingInsights(true);
    setInsightsError(null);
    try {
      const fn = httpsCallable(functions, 'generateDashboardInsightsNow');
      await fn({ force: true });
    } catch (err) {
      setInsightsError(err instanceof Error ? err.message : String(err));
    } finally {
      setRefreshingInsights(false);
    }
  }

  function toggleDrilldown(next: DrilldownSelection) {
    setDrilldown((cur) =>
      cur?.kind === next.kind && cur?.key === next.key ? null : next,
    );
  }

  const maxFunnelCount = Math.max(1, ...aggregates.funnel.map((f) => f.count));
  const maxMissing = Math.max(
    1,
    ...aggregates.missingMatrix.map((m) => m.count),
  );

  return (
    <div style={{ fontFamily: FONT }}>
      <header style={{ marginBottom: 20, fontFamily: FONT }}>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: '#0f172a',
            letterSpacing: -0.3,
            margin: 0,
          }}
        >
          Pipeline dashboard
        </h1>
        <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>
          Aggregated view across {aggregates.kpis.totalDeals} open deals ·{' '}
          {fmtCurrency(aggregates.kpis.totalArr)} total ARR
        </p>
      </header>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 340px',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 12,
          }}
        >
          <KpiTile
            label="Total pipeline ARR"
            value={fmtCurrency(aggregates.kpis.totalArr)}
            sub={`${aggregates.kpis.totalDeals} deals`}
            active={drilldown?.kind === 'kpi' && drilldown.key === 'all'}
            onClick={() =>
              toggleDrilldown({ kind: 'kpi', key: 'all', title: 'All deals' })
            }
            pipelineLink="/pipeline?view=all"
          />
          <KpiTile
            label="Open deals"
            value={String(aggregates.kpis.totalDeals)}
            sub={`${fmtCurrency(aggregates.kpis.weightedArr)} weighted`}
            active={drilldown?.kind === 'kpi' && drilldown.key === 'all'}
            onClick={() =>
              toggleDrilldown({ kind: 'kpi', key: 'all', title: 'All deals' })
            }
            pipelineLink="/pipeline?view=all"
          />
          <KpiTile
            label="At risk"
            value={String(aggregates.kpis.atRiskCount)}
            sub="health < 50"
            accent="#b91c1c"
            active={drilldown?.kind === 'kpi' && drilldown.key === 'at-risk'}
            onClick={() =>
              toggleDrilldown({
                kind: 'kpi',
                key: 'at-risk',
                title: 'At-risk deals',
              })
            }
            pipelineLink="/pipeline?view=risk"
          />
          <KpiTile
            label="Stalled"
            value={String(aggregates.kpis.stalledCount)}
            sub="no activity >21d"
            accent="#a16207"
            active={drilldown?.kind === 'kpi' && drilldown.key === 'stalled'}
            onClick={() =>
              toggleDrilldown({
                kind: 'kpi',
                key: 'stalled',
                title: 'Stalled deals',
              })
            }
            pipelineLink="/pipeline?view=stalled"
          />
        </div>

        <InsightsCard
          insights={insights}
          loading={insightsLoading}
          refreshing={refreshingInsights}
          error={insightsError}
          onRefresh={refreshInsights}
          accountById={accountById}
        />
      </div>

      {drilldown && (
        <DrilldownPanel
          title={drilldown.title}
          accounts={drilldownAccounts}
          pipelineLink={pipelineLinkForDrilldown(drilldown)}
          onClose={() => setDrilldown(null)}
        />
      )}

      <SectionCard title="Stage funnel">
        {aggregates.funnel.map((f) => {
          const meta = stageMeta[f.stage];
          const pct = (f.count / maxFunnelCount) * 100;
          const active =
            drilldown?.kind === 'funnel' && drilldown.key === f.stage;
          return (
            <button
              key={f.stage}
              type="button"
              onClick={() =>
                toggleDrilldown({
                  kind: 'funnel',
                  key: f.stage,
                  title: `${meta.label} deals`,
                })
              }
              style={{
                display: 'block',
                width: '100%',
                border: 'none',
                background: active ? '#eef2ff' : 'transparent',
                borderRadius: 8,
                padding: '8px 10px',
                marginBottom: 6,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <p
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 12,
                  color: '#475569',
                  margin: '0 0 6px',
                }}
              >
                <span style={{ fontWeight: 600, color: meta.color }}>
                  {meta.label}
                </span>
                <span>
                  {f.count} · {fmtCurrency(f.totalArr)}
                  <PipelineLink
                    to={`/pipeline?stage=${f.stage}`}
                    style={{ marginLeft: 8 }}
                  />
                </span>
              </p>
                <div
                  style={{
                    height: 10,
                    background: '#f1f5f9',
                    borderRadius: 999,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${pct}%`,
                      height: '100%',
                      background: meta.dot,
                      borderRadius: 999,
                      transition: 'width .2s',
                    }}
                  />
                </div>
            </button>
          );
        })}
      </SectionCard>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          marginTop: 16,
        }}
      >
        <SectionCard title="Owner leaderboard">
          {aggregates.leaderboard.length === 0 ? (
            <EmptyHint loading={loading} />
          ) : (
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
            >
              <thead>
                <tr style={{ color: '#94a3b8', textAlign: 'left' }}>
                  <th style={{ padding: '6px 4px', fontWeight: 600 }}>Owner</th>
                  <th
                    style={{
                      padding: '6px 4px',
                      fontWeight: 600,
                      textAlign: 'right',
                    }}
                  >
                    Deals
                  </th>
                  <th
                    style={{
                      padding: '6px 4px',
                      fontWeight: 600,
                      textAlign: 'right',
                    }}
                  >
                    ARR
                  </th>
                  <th
                    style={{
                      padding: '6px 4px',
                      fontWeight: 600,
                      textAlign: 'right',
                    }}
                  >
                    Avg health
                  </th>
                  <th
                    style={{
                      padding: '6px 4px',
                      fontWeight: 600,
                      textAlign: 'right',
                    }}
                  >
                    At risk
                  </th>
                </tr>
              </thead>
              <tbody>
                {aggregates.leaderboard.map((o) => {
                  const active =
                    drilldown?.kind === 'owner' &&
                    drilldown.key === o.ownerKey;
                  return (
                    <tr
                      key={o.ownerKey}
                      onClick={() =>
                        toggleDrilldown({
                          kind: 'owner',
                          key: o.ownerKey,
                          title: `${o.name}'s deals`,
                        })
                      }
                      style={{
                        cursor: 'pointer',
                        background: active ? '#eef2ff' : 'transparent',
                      }}
                    >
                      <td style={{ padding: '8px 4px' }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <Avatar
                            name={o.name}
                            initials={o.initials}
                            color={o.color}
                            size={20}
                          />
                          <span
                            style={{
                              color: '#0f172a',
                              fontWeight: 500,
                            }}
                          >
                            {o.name}
                          </span>
                          {o.email && (
                            <PipelineLink
                              to={`/pipeline?owner=${encodeURIComponent(o.email)}`}
                            />
                          )}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '8px 4px',
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {o.count}
                      </td>
                      <td style={{ padding: '8px 4px', textAlign: 'right' }}>
                        {fmtCurrency(o.totalArr)}
                      </td>
                      <td style={{ padding: '8px 4px', textAlign: 'right' }}>
                        {o.avgHealth}
                      </td>
                      <td
                        style={{
                          padding: '8px 4px',
                          textAlign: 'right',
                          color: o.atRisk > 0 ? '#b91c1c' : '#64748b',
                          fontWeight: o.atRisk > 0 ? 600 : 400,
                        }}
                      >
                        {o.atRisk}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </SectionCard>

        <SectionCard title="Health distribution">
          <StackedBar
            segments={aggregates.healthBuckets.map((h) => ({
              key: h.band,
              label: h.label,
              count: h.count,
              color: h.color,
              bg: h.bg,
            }))}
          />
          <div
            style={{
              marginTop: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            {aggregates.healthBuckets.map((h) => {
              const active =
                drilldown?.kind === 'health' && drilldown.key === h.band;
              return (
                <button
                  key={h.band}
                  type="button"
                  onClick={() =>
                    toggleDrilldown({
                      kind: 'health',
                      key: h.band,
                      title: `${h.label} deals`,
                    })
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    border: 'none',
                    background: active ? h.bg : '#fafbfc',
                    borderRadius: 8,
                    padding: '8px 10px',
                    cursor: 'pointer',
                    fontSize: 12,
                    width: '100%',
                  }}
                >
                  <span style={{ color: h.color, fontWeight: 600 }}>
                    {h.label}
                  </span>
                  <span style={{ color: '#64748b' }}>
                    {h.count} · {fmtCurrency(h.totalArr)}
                    <PipelineLink
                      to={`/pipeline?view=${h.band === 'at-risk' ? 'risk' : 'all'}`}
                    />
                  </span>
                </button>
              );
            })}
          </div>
        </SectionCard>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          marginTop: 16,
        }}
      >
        <SectionCard title="Top blockers">
          {aggregates.topBlockers.length === 0 ? (
            <EmptyHint loading={loading} text="No blockers recorded" />
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {aggregates.topBlockers.map((b) => {
                const active =
                  drilldown?.kind === 'blocker' && drilldown.key === b.token;
                return (
                  <button
                    key={b.token}
                    type="button"
                    onClick={() =>
                      toggleDrilldown({
                        kind: 'blocker',
                        key: b.token,
                        title: `Blocker: ${b.token}`,
                      })
                    }
                    style={{
                      border: active
                        ? '1px solid #3730a3'
                        : '1px solid #fecaca',
                      background: active ? '#eef2ff' : '#fef2f2',
                      color: '#b91c1c',
                      borderRadius: 999,
                      padding: '4px 10px',
                      fontSize: 11,
                      fontWeight: 500,
                      cursor: 'pointer',
                      maxWidth: '100%',
                      textAlign: 'left',
                    }}
                  >
                    {b.token} ({b.count})
                  </button>
                );
              })}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Missing info">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {aggregates.missingMatrix.map((m) => {
              const pct = (m.count / maxMissing) * 100;
              const active =
                drilldown?.kind === 'missing' && drilldown.key === m.field;
              return (
                <button
                  key={m.field}
                  type="button"
                  onClick={() =>
                    toggleDrilldown({
                      kind: 'missing',
                      key: m.field,
                      title: `Missing ${m.field}`,
                    })
                  }
                  style={{
                    border: 'none',
                    background: active ? '#eef2ff' : 'transparent',
                    borderRadius: 6,
                    padding: '4px 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 11,
                      color: '#475569',
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{m.field}</span>
                    <span>{m.count}</span>
                  </div>
                  <div
                    style={{
                      height: 6,
                      background: '#f1f5f9',
                      borderRadius: 999,
                      overflow: 'hidden',
                    }}
                  >
                      <div
                        style={{
                          width: `${pct}%`,
                          height: '100%',
                          background: '#3730a3',
                          borderRadius: 999,
                        }}
                      />
                  </div>
                </button>
              );
            })}
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Activity buckets" style={{ marginTop: 16 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 10,
          }}
        >
          {aggregates.activity.map((a) => {
            const active =
              drilldown?.kind === 'activity' && drilldown.key === a.bucket;
            return (
              <button
                key={a.bucket}
                type="button"
                onClick={() =>
                  toggleDrilldown({
                    kind: 'activity',
                    key: a.bucket,
                    title: a.label,
                  })
                }
                style={{
                  border: active ? '1px solid #3730a3' : '1px solid #e8ebf0',
                  background: active ? '#eef2ff' : 'white',
                  borderRadius: 10,
                  padding: '12px 14px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    marginBottom: 4,
                  }}
                >
                  {a.label}
                </div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 600,
                    color: '#0f172a',
                  }}
                >
                  {a.count}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                  {fmtCurrency(a.totalArr)}
                </div>
              </button>
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
}

function pipelineLinkForDrilldown(sel: DrilldownSelection): string {
  switch (sel.kind) {
    case 'kpi':
      if (sel.key === 'at-risk') return '/pipeline?view=risk';
      if (sel.key === 'stalled') return '/pipeline?view=stalled';
      return '/pipeline?view=all';
    case 'funnel':
      return `/pipeline?stage=${sel.key}`;
    case 'owner': {
      const row = sel.key;
      return row.includes('@')
        ? `/pipeline?owner=${encodeURIComponent(row)}`
        : '/pipeline?view=all';
    }
    default:
      return '/pipeline?view=all';
  }
}

function KpiTile({
  label,
  value,
  sub,
  accent,
  active,
  onClick,
  pipelineLink,
}: {
  label: string;
  value: string;
  sub: string;
  accent?: string;
  active?: boolean;
  onClick: () => void;
  pipelineLink: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: active ? '1px solid #3730a3' : '1px solid #e8ebf0',
        background: active ? '#eef2ff' : 'white',
        borderRadius: 12,
        padding: '14px 16px',
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: FONT,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
        <div
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: accent ?? '#0f172a',
            letterSpacing: -0.3,
          }}
        >
          {value}
        </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 4,
          fontSize: 11,
          color: '#64748b',
        }}
      >
        <span>{sub}</span>
        <PipelineLink to={pipelineLink} />
      </div>
    </button>
  );
}

function PipelineLink({
  to,
  style,
}: {
  to: string;
  style?: CSSProperties;
}) {
  return (
    <Link
      to={to}
      onClick={(e) => e.stopPropagation()}
      style={{
        fontSize: 10,
        color: '#3730a3',
        fontWeight: 600,
        textDecoration: 'none',
        marginLeft: style ? undefined : 6,
        ...style,
      }}
    >
      See in pipeline →
    </Link>
  );
}

function SectionCard({
  title,
  children,
  style,
}: {
  title: string;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        background: 'white',
        borderRadius: 12,
        border: '1px solid #e8ebf0',
        padding: 16,
        fontFamily: FONT,
        ...style,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.5,
          color: '#94a3b8',
          textTransform: 'uppercase',
          marginBottom: 12,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function StackedBar({
  segments,
}: {
  segments: Array<{
    key: string;
    label: string;
    count: number;
    color: string;
    bg: string;
  }>;
}) {
  const total = Math.max(
    1,
    segments.reduce((s, seg) => s + seg.count, 0),
  );
  return (
    <div
      style={{
        display: 'flex',
        height: 14,
        borderRadius: 999,
        overflow: 'hidden',
        background: '#f1f5f9',
      }}
    >
      {segments.map((seg) =>
        seg.count > 0 ? (
          <div
            key={seg.key}
            title={`${seg.label}: ${seg.count}`}
            style={{
              width: `${(seg.count / total) * 100}%`,
              background: seg.color,
              minWidth: seg.count > 0 ? 4 : 0,
            }}
          />
        ) : null,
      )}
    </div>
  );
}

function DrilldownPanel({
  title,
  accounts,
  pipelineLink,
  onClose,
}: {
  title: string;
  accounts: PipelineAccount[];
  pipelineLink: string;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #3730a3',
        borderRadius: 12,
        marginBottom: 16,
        overflow: 'hidden',
        fontFamily: FONT,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          background: '#eef2ff',
          borderBottom: '1px solid #c7d2fe',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: '#3730a3' }}>
          {title} ({accounts.length})
        </span>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <PipelineLink to={pipelineLink} />
          <button
            type="button"
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#64748b',
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            Close
          </button>
        </div>
      </div>
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {accounts.length === 0 ? (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                color: '#94a3b8',
                fontSize: 13,
              }}
            >
              No accounts in this slice.
            </div>
        ) : (
          accounts.map((a) => {
            const score = a.hasAnalysis ? computeHealth(a) : null;
            const band = score != null ? healthBand(score) : null;
            return (
              <Link
                key={a.id}
                to={`/accounts/${a.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 16px',
                  borderBottom: '1px solid #f1f5f9',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <Logo color={a.logoColor} label={a.logo} size={28} radius={6} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#0f172a',
                    }}
                  >
                    {a.name}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginTop: 2,
                      fontSize: 11,
                      color: '#64748b',
                    }}
                  >
                    <StageChip stage={a.stage} />
                    <span>{fmtCurrency(a.arr)}</span>
                    <span>{fmtRelative(a.lastActivity)}</span>
                  </div>
                </div>
                {band && score != null && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: band.color,
                    }}
                  >
                    {score}
                  </span>
                )}
                <Avatar
                  name={a.owner.name}
                  initials={a.owner.initials}
                  color={a.owner.color}
                  size={22}
                />
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}

function InsightsCard({
  insights,
  loading,
  refreshing,
  error,
  onRefresh,
  accountById,
}: {
  insights: DashboardInsightsDoc | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  onRefresh: () => void;
  accountById: Map<string, PipelineAccount>;
}) {
  const kindColor: Record<string, { bg: string; fg: string }> = {
    risk: { bg: '#fef2f2', fg: '#b91c1c' },
    opportunity: { bg: '#ecfdf5', fg: '#047857' },
    focus: { bg: '#eef2ff', fg: '#3730a3' },
  };

  return (
    <div
      style={{
        background: 'white',
        borderRadius: 12,
        border: '1px solid #e8ebf0',
        padding: 16,
        fontFamily: FONT,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.5,
              color: '#94a3b8',
              textTransform: 'uppercase',
            }}
          >
            AI portfolio insights
          </div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                {insights?.generatedAt
                  ? `Updated ${fmtRelative(insights.generatedAt.toDate())}`
                  : loading
                    ? 'Loading…'
                    : 'Not generated yet'}
              </div>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="btn-secondary"
          style={{ fontSize: 11, padding: '4px 10px' }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh now'}
        </button>
      </div>

      {error && (
        <div
          style={{
            fontSize: 11,
            color: '#b91c1c',
            background: '#fef2f2',
            padding: '8px 10px',
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', maxHeight: 220 }}>
        {!loading && !insights?.insights?.length && (
          <p
            style={{
              fontSize: 12,
              color: '#94a3b8',
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            No insights yet. Click Refresh now to generate a portfolio snapshot
            from the current pipeline.
          </p>
        )}
        {(insights?.insights ?? []).map((item, i) => {
          const colors = kindColor[item.kind] ?? kindColor.focus;
          return (
            <div
              key={i}
              style={{
                padding: '10px 0',
                borderBottom:
                  i < (insights?.insights.length ?? 0) - 1
                    ? '1px solid #f1f5f9'
                    : 'none',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    padding: '2px 6px',
                    borderRadius: 4,
                    background: colors.bg,
                    color: colors.fg,
                  }}
                >
                  {item.kind}
                </span>
                <span
                  style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}
                >
                  {item.title}
                </span>
              </div>
              <p
                style={{
                  fontSize: 12,
                  color: '#475569',
                  margin: '0 0 6px',
                  lineHeight: 1.45,
                }}
              >
                {item.body}
              </p>
              {item.accountIds.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {item.accountIds.slice(0, 5).map((id) => {
                    const acct = accountById.get(id);
                    return (
                      <Link
                        key={id}
                        to={`/accounts/${id}`}
                        style={{
                          fontSize: 10,
                          color: '#3730a3',
                          background: '#eef2ff',
                          padding: '2px 6px',
                          borderRadius: 4,
                          textDecoration: 'none',
                        }}
                      >
                        {acct?.name ?? id.slice(0, 8)}
                      </Link>
                    );
                  })}
                  {item.accountIds.length > 5 && (
                    <span style={{ fontSize: 10, color: '#94a3b8' }}>
                      +{item.accountIds.length - 5} more
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EmptyHint({
  loading,
  text = 'No data yet',
}: {
  loading: boolean;
  text?: string;
}) {
  return (
    <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
      {loading ? 'Loading…' : text}
    </p>
  );
}
